import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Sheet } from '../../../components/ui/Sheet';
import { DESIGNATION_COLORS, DESIGNATION_SHORT } from '../designation';
import { formatIst } from '../formatIst';
import { useResearchHistory } from '../hooks/useResearchHistory';
import type {
  ResearchFieldChange,
  ResearchGroupInfo,
  ResearchVersion,
} from '../../../services/dashboard.service';

/** A stored value as text; empty and placeholder values read as "—". */
const showValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map(showValue).join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export function BucketTag({ category }: { category: string | null }) {
  if (!category) return <span className="text-slate-400">No bucket</span>;
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: DESIGNATION_COLORS[category] ?? '#94a3b8' }}
      />
      <span className="truncate">{DESIGNATION_SHORT[category] ?? category}</span>
    </span>
  );
}

function ValueChange({ c }: { c: ResearchFieldChange }) {
  const isList = c.added_items !== undefined;
  return (
    <div className="py-2 border-t border-slate-100 first:border-t-0">
      <div className="text-[11px] font-bold text-slate-600 mb-1">{c.label}</div>
      {isList ? (
        <div className="flex flex-wrap gap-1">
          {c.added_items?.map((item, i) => (
            <span key={`a${i}`} className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px]">
              + {showValue(item)}
            </span>
          ))}
          {c.removed_items?.map((item, i) => (
            <span key={`r${i}`} className="px-1.5 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-[11px] line-through decoration-rose-300">
              − {showValue(item)}
            </span>
          ))}
        </div>
      ) : (
        <div className="flex items-start gap-2 text-xs">
          <span className={`min-w-0 break-words ${c.change === 'added' ? 'text-slate-400' : 'text-rose-700 line-through decoration-rose-300'}`}>
            {showValue(c.old)}
          </span>
          <ArrowRight size={12} className="text-slate-400 shrink-0 mt-0.5" />
          <span className={`min-w-0 break-words font-semibold ${c.change === 'removed' ? 'text-slate-400' : 'text-emerald-700'}`}>
            {showValue(c.new)}
          </span>
        </div>
      )}
    </div>
  );
}

function Reworded({ changes }: { changes: ResearchFieldChange[] }) {
  const [open, setOpen] = useState(false);
  if (!changes.length) return null;
  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-700"
      >
        {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
        Reworded ({changes.length})
        <span className="font-medium text-slate-400">· text the AI rewrote</span>
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          {changes.map((c) => (
            <div key={c.path}>
              <div className="text-[11px] font-bold text-slate-600 mb-1">{c.label}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] leading-relaxed">
                <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 text-slate-500">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">Before</div>
                  {showValue(c.old)}
                </div>
                <div className="p-2 rounded-lg bg-white border border-slate-200 text-slate-700">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">After</div>
                  {showValue(c.new)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VersionCard({ v, groups }: { v: ResearchVersion; groups: ResearchGroupInfo[] }) {
  const values = v.changes.filter((c) => c.kind === 'value');
  const reworded = v.changes.filter((c) => c.kind === 'text');
  const changedGroups = groups.filter((g) => values.some((c) => c.group === g.key));
  const unchanged = groups.filter((g) => g.key !== 'other' && !changedGroups.includes(g));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
        <span className="font-bold text-slate-800 text-xs">{formatIst(v.researched_at ?? v.recorded_at)}</span>
        {v.kind === 'run' && (
          <span className="px-1.5 py-0.5 rounded-md border border-slate-200 bg-slate-50 text-[9px] font-bold uppercase tracking-wider">
            {v.trigger === 'scheduled' ? 'Scheduled' : 'Manual'}
          </span>
        )}
        {v.researched_by && <span>by {v.researched_by}</span>}
        <span className="ml-auto"><BucketTag category={v.designation_category} /></span>
      </div>

      {v.kind === 'baseline' ? (
        <p className="mt-2 text-xs text-slate-500">
          Research this profile had before history was kept. Later runs are compared with it.
        </p>
      ) : v.first_research ? (
        <p className="mt-2 text-xs text-slate-500">First AI research for this profile — nothing to compare with.</p>
      ) : !v.changes.length ? (
        <p className="mt-2 text-xs text-slate-500">Nothing changed.</p>
      ) : (
        <>
          {changedGroups.map((g) => (
            <div key={g.key} className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-accent mb-1">{g.label}</div>
              <div className="rounded-xl border border-slate-100 px-3">
                {values.filter((c) => c.group === g.key).map((c) => <ValueChange key={c.path} c={c} />)}
              </div>
            </div>
          ))}
          {!values.length && <p className="mt-2 text-xs text-slate-500">No values changed; only wording.</p>}
          {values.length > 0 && unchanged.length > 0 && (
            <p className="mt-3 text-[11px] text-slate-400">
              No change in: {unchanged.map((g) => g.label).join(', ')}
            </p>
          )}
          <Reworded changes={reworded} />
        </>
      )}
    </div>
  );
}

interface ResearchHistorySheetProps {
  /** Profile _id or donor id; the sheet is closed while null. */
  alumniId: string | null;
  name?: string | null;
  onClose: () => void;
  /** Show the "Open profile" link (off when already on the profile page). */
  linkToProfile?: boolean;
}

/** Every AI research run of one profile and what each changed, newest first. */
export function ResearchHistorySheet({ alumniId, name, onClose, linkToProfile = true }: ResearchHistorySheetProps) {
  const { data, loading, error } = useResearchHistory(alumniId);
  const navigate = useNavigate();
  const versions = data?.versions ?? [];

  return (
    <Sheet isOpen={!!alumniId} onClose={onClose} title="AI research history" maxWidth="max-w-2xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-outfit font-extrabold text-lg text-slate-800 truncate">{name || 'Alumni'}</div>
            {data && (
              <div className="text-[11px] text-slate-400">
                {versions.length} version{versions.length === 1 ? '' : 's'} kept · values in green are the newer ones
              </div>
            )}
          </div>
          {linkToProfile && alumniId && (
            <button
              onClick={() => navigate(`/alumni/${alumniId}`)}
              className="text-xs font-bold text-accent hover:underline shrink-0"
            >
              Open profile
            </button>
          )}
        </div>

        {loading && <p className="text-xs text-slate-400">Loading history…</p>}
        {error && <p className="text-xs text-rose-600">{error}</p>}
        {data && !versions.length && (
          <p className="text-xs text-slate-500">
            No AI research recorded yet. History starts with this profile's next AI research run.
          </p>
        )}
        {data && versions.map((v) => <VersionCard key={v._id} v={v} groups={data.groups} />)}
      </div>
    </Sheet>
  );
}
